import { useCallback, useEffect, useMemo } from 'react';
import WebApp from '../../utils/WebAppPolyfill';
import { CHAIN } from '@tonconnect/sdk';
import { TonConnectButton, useTonConnectUI, useTonWallet } from '@tonconnect/ui-react';
import { Address } from '@ton/core';
import { postFormData } from '../../network/request';

/** localStorage key：其它模块可 JSON.parse 读取 { chain_key, public_key, wallet_status, address, wallet_name } */
export const MINE_TON_WALLET_SNAPSHOT_KEY = 'mineTonWalletSnapshot';

function chainLabel(chain) {
    if (chain === CHAIN.MAINNET) return 'TON Mainnet';
    if (chain === CHAIN.TESTNET) return 'TON Testnet';
    return chain || '—';
}

function formatPublicKey(hex) {
    if (!hex || typeof hex !== 'string') return '—';
    const t = hex.trim();
    if (t.length <= 18) return t;
    return `${t.slice(0, 10)}…${t.slice(-8)}`;
}

function formatAddress(raw) {
    if (!raw) return '—';
    try {
        const a = Address.parse(raw);
        const s = a.toString({ bounceable: false });
        if (s.length <= 14) return s;
        return `${s.slice(0, 8)}…${s.slice(-6)}`;
    } catch {
        return raw.length > 14 ? `${raw.slice(0, 8)}…${raw.slice(-6)}` : raw;
    }
}

export default function MineWalletSection() {
    const wallet = useTonWallet();
    const [tonConnectUI] = useTonConnectUI();

    const walletSnapshot = useMemo(() => {
        if (!wallet) {
            return {
                chain_key: '',
                public_key: '',
                wallet_status: 'disconnected',
                address: '',
                wallet_name: '',
            };
        }
        const { chain, publicKey, address } = wallet.account;
        return {
            chain_key: chain ?? '',
            public_key: publicKey ?? '',
            wallet_status: 'connected',
            address: address ?? '',
            wallet_name: wallet.name ?? '',
        };
    }, [wallet]);

    useEffect(() => {
        try {
            localStorage.setItem(MINE_TON_WALLET_SNAPSHOT_KEY, JSON.stringify(walletSnapshot));
        } catch {
            /* ignore quota / private mode */
        }
    }, [walletSnapshot]);

    // 同步钱包信息到后端
    useEffect(() => {
        const syncWalletToServer = async () => {
            const initUserStr = localStorage.getItem('initUser');
            if (!initUserStr) return;

            try {
                const initUser = JSON.parse(initUserStr);
                const userId = initUser.user_id;
                if (!userId) return;

                const formData = new FormData();
                formData.append('userId', userId);
                formData.append('chainKey', walletSnapshot.chain_key || '');
                formData.append('publicKey', walletSnapshot.public_key || '');
                formData.append('walletStatus', walletSnapshot.wallet_status || 'disconnected');

                await postFormData('/updateWalletInfo', formData);
            } catch (error) {
                console.error('同步钱包信息失败:', error);
            }
        };

        syncWalletToServer();
    }, [walletSnapshot]);

    const fullAddress = useMemo(() => {
        if (!wallet?.account?.address) return '';
        try {
            return Address.parse(wallet.account.address).toString({ bounceable: false });
        } catch {
            return wallet.account.address;
        }
    }, [wallet]);

    const disconnect = useCallback(() => {
        tonConnectUI.disconnect();
    }, [tonConnectUI]);

    const openTelegramWallet = useCallback(() => {
        try {
            WebApp.openTelegramLink('https://t.me/wallet');
        } catch {
            window.open('https://t.me/wallet', '_blank', 'noopener,noreferrer');
        }
    }, []);

    return (
        <div className="mineWalletShell">
            <p className="menuGroupTitle mineWalletGroupTitle">Wallet</p>
            <div className="mineWalletCard">
                <div className="mineWalletTop">
                    <div className="mineWalletIconWrap" aria-hidden>
                        <svg className="mineWalletIcon" viewBox="0 0 24 24" fill="none">
                            <path
                                d="M12 2L4 6v6c0 5 3.5 9.5 8 11 4.5-1.5 8-6 8-11V6l-8-4z"
                                stroke="currentColor"
                                strokeWidth="1.75"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M9 12l2 2 4-5"
                                stroke="currentColor"
                                strokeWidth="1.75"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                    <div className="mineWalletTopText">
                        <h2 className="mineWalletTitle">TON Wallet</h2>
                        <p className="mineWalletSubtitle">
                            Connect via TonConnect — choose <strong>Telegram Wallet</strong> in the list to use Telegram&apos;s built-in wallet.
                        </p>
                    </div>
                </div>

                {wallet ? (
                    <div className="mineWalletBody">
                        <div className="mineWalletStatus">
                            <span className="mineWalletStatusDot" aria-hidden />
                            <span className="mineWalletStatusLabel">Connected</span>
                        </div>
                        <p className="mineWalletAppName">{wallet.name}</p>
                        <p className="mineWalletAddress" title={fullAddress}>
                            {formatAddress(wallet.account.address)}
                        </p>
                        <dl className="mineWalletMeta">
                            <div className="mineWalletMetaRow">
                                <dt className="mineWalletMetaKey">wallet_status</dt>
                                <dd className="mineWalletMetaVal mineWalletMetaValOk">
                                    {walletSnapshot.wallet_status}
                                </dd>
                            </div>
                            <div className="mineWalletMetaRow">
                                <dt className="mineWalletMetaKey">chain_key</dt>
                                <dd
                                    className="mineWalletMetaVal"
                                    title={walletSnapshot.chain_key || undefined}
                                >
                                    {walletSnapshot.chain_key
                                        ? `${walletSnapshot.chain_key} (${chainLabel(walletSnapshot.chain_key)})`
                                        : '—'}
                                </dd>
                            </div>
                            <div className="mineWalletMetaRow">
                                <dt className="mineWalletMetaKey">public_key</dt>
                                <dd
                                    className="mineWalletMetaVal"
                                    title={walletSnapshot.public_key || undefined}
                                >
                                    {walletSnapshot.public_key
                                        ? formatPublicKey(walletSnapshot.public_key)
                                        : 'Not provided by wallet'}
                                </dd>
                            </div>
                        </dl>
                        <div className="mineWalletActions">
                            <button type="button" className="mineWalletBtn mineWalletBtnGhost" onClick={disconnect}>
                                Disconnect
                            </button>
                            <div className="mineTonConnectHost">
                                <TonConnectButton className="mineTonConnectButton" />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="mineWalletBody">
                        <p className="mineWalletDisconnectedMeta">
                            <span className="mineWalletMetaKey">wallet_status</span>{' '}
                            <span className="mineWalletMetaValMuted">disconnected</span>
                        </p>
                        <p className="mineWalletHint">
                            Link your on-chain wallet to use TON in this app. In Telegram, pick <strong>Telegram Wallet</strong> when the wallet list opens.
                        </p>
                        <div className="mineWalletActions mineWalletActionsStack">
                            <div className="mineTonConnectHost mineTonConnectHostFull">
                                <TonConnectButton className="mineTonConnectButton mineTonConnectButtonPrimary" />
                            </div>
                            <button type="button" className="mineWalletBtn mineWalletBtnOutline" onClick={openTelegramWallet}>
                                Open @wallet in Telegram
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
